/* Session — deux publics, deux portes de connexion cloisonnées.

   La console ops (/login) et l'espace annonceur (/portal/login) ont chacun leur
   formulaire, et `login()` transmet la porte utilisée : le backend n'authentifie
   que la population correspondante.

   Le backend renvoie un `scope` avec l'utilisateur : "admin" (console ops) ou
   "partner" (portail annonceur). C'est lui qui décide de la destination après
   login et de la zone protégée à laquelle le compte a droit ; on ne devine
   jamais à partir du rôle. Les comptes émis avant l'ajout du champ n'en ont
   pas : ils sont traités comme admin, leur seul usage possible. */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";

const AuthCtx = createContext({ user: undefined, login: () => {}, logout: () => {} });

export const ADMIN_SCOPE = "admin";
export const PARTNER_SCOPE = "partner";

/** Écran d'accueil d'un compte selon son scope. */
export function homePath(user) {
  return user?.scope === PARTNER_SCOPE ? "/portal" : "/admin";
}

/** Page de connexion desservant une zone protégée. */
export const ADMIN_LOGIN_PATH = "/login";
export const PARTNER_LOGIN_PATH = "/portal/login";

export function loginPathFor(scope) {
  return scope === PARTNER_SCOPE ? PARTNER_LOGIN_PATH : ADMIN_LOGIN_PATH;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anon, obj = logged in

  useEffect(() => {
    const token = sessionStorage.getItem("gocab_token");
    if (!token) {
      setUser(null);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const login = useCallback(async (email, password, scope = ADMIN_SCOPE) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, scope });
      sessionStorage.setItem("gocab_token", data.token);
      sessionStorage.setItem("gocab_user", JSON.stringify(data.user));
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e) };
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem("gocab_token");
    sessionStorage.removeItem("gocab_user");
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
